<script>
  import { tick, setContext, onMount } from "svelte";
  import { writable } from "svelte/store";
  import cloneDeep from "lodash/cloneDeep";
  import isEmpty from "lodash/isEmpty";
  import { navigate } from "svelte-routing";
  import { Modal } from "svelte-utilities";

  import XIcon from "@local/assets/icons/x.svg";
  import GridIcon from "@local/assets/icons/table.svg";
  import FileMultipleIcon from "@local/assets/icons/file-multiple.svg";
  import FileVirtualIcon from "@local/assets/icons/file-virtual.svg";
  import VideoIcon from "@local/assets/icons/video.svg";
  import ShareIcon from "@local/assets/icons/share.svg";
  import FileIcon from "@local/assets/icons/file.svg";
  import ListThumbnail from "#lib/ListThumbnail.svelte";
  import DocumentThumbnail from "#lib/DocumentThumbnail.svelte";
  import SortableThumbnailList from "#lib/SortableThumbnailList.svelte";
  import Sidebar from "#lib/sidebar/Sidebar.svelte";
  import JobProperties from "#lib/sidebar/JobProperties.svelte";
  import DocumentProperties from "#lib/sidebar/DocumentProperties.svelte";
  import RemoteDocProperties from "#lib/sidebar/RemoteDocProperties.svelte";
  import SidebarSection from "#lib/sidebar/SidebarSection.svelte";
  import ButtonInput from "#lib/sidebar/ButtonInput.svelte";
  import Attachments from "#lib/sidebar/Attachments.svelte";
  import History from "#lib/sidebar/History.svelte";
  import PrevNext from "#lib/sidebar/PrevNext.svelte";
  import SidebarTitle from "#lib/sidebar/SidebarTitle.svelte";
  import CountGrid from "#lib/CountGrid.svelte";
  import NewDocumentModal from "#lib/NewDocumentModal.svelte";

  import { selectedDocuments as selectedDocumentsObj, pathRoot, showRightPanel } from "#src/stores/ui.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import locationsByDocument from "#src/extensions/locations-by-document.js";
  import eb from "#src/extensions/event-bus.js";

  export let group;
  export let documentNotes;
  export let disabled;

  let width;
  let height;

  let cvwidth;
  let cvheight;

  let confirmRemoveModal;
  let docsToDelete = [];
  let currentVideo = null;

  const fileData = writable({});

  setContext("fileData", fileData);

  $: attachments = $group.attachments.filter((a) => !a.type_id && !a.item_id);
  $: documents = orderedList($group.documents);
  $: selectedDocs = documents.filter((d) => $selectedDocumentsObj[d.id]);
  $: selectedDoc = selectedDocs[0];
  $: sourceDocument = getSourceDoc(selectedDoc?.id, $group);
  $: selectedDocIndex = documents.indexOf(selectedDocs[0]);
  $: docLocations = locationsByDocument($group);
  $: prevDoc = selectedDocs.length === 1 && documents[selectedDocIndex - 1];
  $: nextDoc = selectedDocs.length === 1 && documents[selectedDocIndex + 1];

  function selectNone() {
    $selectedDocumentsObj = {};
  }

  function getSourceDoc(docid, grp) {
    if (!docid) return null;
    const d = grp.documents[docid];
    if (!d) return null;
    if (!d?.is_virtual) return d;
    const sourceDoc = grp.documents[d.source_document_id];
    if (!sourceDoc) return null;
    return sourceDoc;
  }

  function selectDocument(e) {
    const { id, event } = e.detail;
    if (event.shiftKey && !isEmpty($selectedDocumentsObj)) {
      const allIds = documents.map((t) => t.id);
      const indices = documents
        .map((t, i) => i)
        .filter((i) => $selectedDocumentsObj[documents[i].id] || documents[i].id === id);

      const min = Math.min(...indices);
      const max = Math.max(...indices);

      const ids = allIds.slice(min, max + 1);
      selectedDocumentsObj.selectOnly(...ids);
    } else if (discontiguousKey(event)) {
      if (!$selectedDocumentsObj[id]) {
        selectedDocumentsObj.select(id);
      } else {
        selectedDocumentsObj.deselect(id);
      }
    } else {
      selectedDocumentsObj.selectOnly(id);
    }
  }

  function confirmRemove(e) {
    const { selected } = e.detail;
    docsToDelete = selected.filter((id) => $group.documents[id]);

    const ddt = docsToDelete.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
    const related = $group.documents.order.filter((id) => {
      const doc = $group.documents[id];
      return doc?.is_virtual && ddt[doc?.source_document_id] && !ddt[id];
    });
    docsToDelete = [...docsToDelete, ...related];

    confirmRemoveModal.open();
  }

  async function removeDocuments() {
    const dtd = [...docsToDelete];
    docsToDelete = [];
    await tick();
    group.removeDocument(...dtd);
  }

  function cloneDocuments(d) {
    const { selected } = d.detail;

    const cloned = selected.map((id) => {
      const doc = cloneDeep($group.documents[id]);
      doc.id = crypto.randomUUID();
      return doc;
    });
    group.addDocument(...cloned);
  }

  function beginAddingDocument() {
    eb.dispatch("open-create-document-modal");
  }

  function updateDocument(e) {
    const { id, diff } = e.detail;
    group.updateDocument(id, diff);
  }

  function reorder(e) {
    const { insertion, dragging } = e.detail;
    const order = cloneDeep($group.documents.order);
    const moved = order.splice(dragging, 1)[0];
    order.splice(insertion, 0, moved);
    group.updateDocumentOrder(order);
  }

  function gotoPrev() {
    if (prevDoc) selectedDocumentsObj.selectOnly(prevDoc.id);
  }

  function gotoNext() {
    if (nextDoc) selectedDocumentsObj.selectOnly(nextDoc.id);
  }

  function nav(e) {
    const { id } = e.detail;
    const doc = $group.documents[id];
    if (!doc) return;

    if (doc.type === "remote") {
      if (doc.data.embed) {
        currentVideo = doc.data.embed;
      } else {
        window.open(doc.data.url, "_blank");
      }
    } else {
      const newpath = `${$pathRoot}documents/${id}`;
      navigate(newpath);
    }
  }

  onMount(() => {
    if (!documents.length && !$group.items.order.length) {
      eb.dispatch("open-create-document-modal", { firstRun: true });
    }
  });
</script>

<div class="bg-gray-100 h-full flex" bind:offsetWidth={width} bind:offsetHeight={height}>
  <SortableThumbnailList
    list={documents}
    {disabled}
    selected={$selectedDocumentsObj}
    on:add={beginAddingDocument}
    on:select={selectDocument}
    on:remove-selected={confirmRemove}
    on:select-none={selectNone}
    on:clone-selected={cloneDocuments}
    on:nav={nav}
    on:reorder={reorder}
    let:record>
    <ListThumbnail {record} {group} slot="default">
      <DocumentThumbnail doc={record} {group} on:update={updateDocument} slot="default" />
      <div slot="caption" class="grow flex justify-between gap-2 overflow-hidden">
        <div class="grow truncate">
          {record.name}
        </div>
        <div>
          {#if record.type === "imported"}
            <FileMultipleIcon />
          {:else if record.type === "virtual"}
            <FileVirtualIcon />
          {:else if record.type === "remote"}
            {#if record.data.doctype === "video"}
              <VideoIcon />
            {:else if record.data.doctype === "spreadsheet"}
              <ShareIcon />
            {:else}
              <ShareIcon />
            {/if}
          {/if}
        </div>
      </div>
    </ListThumbnail>
    <div slot="add" class="text-center text-sm">
      <div class="flex gap-1 items-center">
        <FileIcon />
        <div>Add Document</div>
      </div>
    </div>
  </SortableThumbnailList>

  {#if $showRightPanel && width >= 640}
    <Sidebar>
      <svelte:fragment slot="header">
        {#if selectedDocs.length === 1 && selectedDoc}
          <PrevNext
            prev={prevDoc}
            next={nextDoc}
            title={selectedDoc.name}
            on:gotoprev={gotoPrev}
            on:gotonext={gotoNext}
            sticky />
        {/if}
      </svelte:fragment>
      <svelte:fragment slot="content">
        {#if selectedDocs.length === 0}
          <SidebarTitle title="Job Properties" />
          <JobProperties {disabled} {group} />
          <Attachments {attachments} {group} {disabled} />
          <SidebarSection>
            <ButtonInput
              label="Count Grid"
              labelWidth="5rem"
              value={null}
              buttons={[{ component: GridIcon, label: "Show Count Grid", value: "grid" }]}
              on:input={() => eb.dispatch("toggle-count-grid")} />
          </SidebarSection>
          <History updatedBy={$group.updater} updatedOn={$group.updated_at} />
        {:else if selectedDocs.length === 1}
          {#key selectedDoc.id}
            {#if selectedDoc.type === "imported"}
              <DocumentProperties origDoc={null} doc={selectedDoc} {group} {disabled} />
            {:else if selectedDoc.type === "virtual"}
              <DocumentProperties origDoc={selectedDoc} doc={sourceDocument} {group} {disabled} />
            {:else if selectedDoc.type === "remote"}
              <RemoteDocProperties document={selectedDoc} {group} {disabled} />
            {/if}
          {/key}
        {:else}
          <SidebarTitle title="Multiple Selected" />
        {/if}
      </svelte:fragment>

      <svelte:fragment slot="footer" />
    </Sidebar>
  {/if}

  {#if currentVideo}
    {@const containerAr = cvwidth ? cvwidth / cvheight : 16 / 9}
    {@const videoAr = currentVideo.width / currentVideo.height}
    {@const videoBoxW = containerAr > videoAr ? cvheight * videoAr : cvwidth}
    {@const videoBoxH = containerAr > videoAr ? cvheight : cvwidth / videoAr}
    <div
      class="absolute w-full h-full modal-container flex items-center justify-center bg-gray-900/25 p-4 z-50">
      <div
        class="rounded-lg overflow-hidden h-full w-full bg-white border border-gray-400 relative flex flex-col">
        <div class="flex justify-between px-4 py-2 border-b text-gray-500 flex-none items-center">
          <div class="grow truncate">
            {currentVideo.title}
          </div>
          <button class="flex-none p-1 rounded hover:bg-gray-200" on:click={() => (currentVideo = null)}>
            <XIcon />
          </button>
        </div>
        <div
          class="grow overflow-hidden flex items-center justify-center"
          bind:clientWidth={cvwidth}
          bind:clientHeight={cvheight}>
          <div style="width:{videoBoxW}px;height:{videoBoxH}px;">
            {@html currentVideo.html}
          </div>
        </div>
      </div>
    </div>
  {/if}
</div>

<NewDocumentModal {group} {documents} />

<CountGrid
  {group}
  {docLocations}
  selectedLocationList={[]}
  {disabled}
  filters={selectedDocs.length === 1 ? selectedDoc?.data?.filters : null}
  docid={selectedDocs.length === 1 ? sourceDocument?.id || selectedDoc.id : null} />

<Modal
  bind:this={confirmRemoveModal}
  on:confirm={removeDocuments}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  closeOnOutclick>
  <div slot="title">Delete Documents</div>
  <div slot="content" class="space-y-2">
    <div class="space-y-2">
      <p>Are you sure you want to delete these documents?</p>
      {#each docsToDelete as id}
        <div class="pl-4 truncate">{$group.documents[id].name}</div>
      {/each}
    </div>
  </div>
</Modal>
